import { type Injector } from '@angular/core';
import { BlockNodes, type IMenuButton } from '@principle-theorem/editor';
import { type MenuButtonLoaderFn } from '@principle-theorem/ng-prosemirror';
import { BasicDialogService } from '@principle-theorem/ng-shared';
import { type Editor } from '@tiptap/core';
import { v4 as uuid } from 'uuid';
import { BasicMenuButtonComponent } from '../../menu-bar/basic-menu-button/basic-menu-button.component';
import {
  RawHtmlDialogComponent,
  createRawHtmlDialogConfig,
  type IRawHtmlDialogData,
  type IRawHtmlDialogOutput,
} from './raw-html-dialog/raw-html-dialog.component';

export function createRawHtmlMenuButton(
  injector: Injector
): MenuButtonLoaderFn<BasicMenuButtonComponent, IMenuButton> {
  return () => ({
    component: BasicMenuButtonComponent,
    data: rawHtmlMenuButtonData('submenu-detail', 'HTML', injector),
  });
}

export function createRawHtmlBlockMenuButton(
  injector: Injector
): MenuButtonLoaderFn<BasicMenuButtonComponent, IMenuButton> {
  return () => ({
    component: BasicMenuButtonComponent,
    data: rawHtmlMenuButtonData('block', 'Raw HTML', injector),
  });
}

function rawHtmlMenuButtonData(
  buttonType: IMenuButton['buttonType'],
  buttonText: string,
  injector: Injector
): IMenuButton {
  return {
    buttonType,
    buttonText,
    tooltip: 'Insert raw HTML',
    icon: 'code',
    isActiveFn: (editor) => editor.isActive(BlockNodes.RawHtml),
    command: (editor) =>
      void insertRawHtmlViaDialog(injector.get(BasicDialogService), editor),
  };
}

async function insertRawHtmlViaDialog(
  dialog: BasicDialogService,
  editor: Editor
): Promise<void> {
  const result = await dialog
    .open<RawHtmlDialogComponent, IRawHtmlDialogData, IRawHtmlDialogOutput>(
      RawHtmlDialogComponent,
      createRawHtmlDialogConfig()
    )
    .afterClosed()
    .toPromise();

  if (!result) {
    return;
  }
  editor
    .chain()
    .focus()
    .insertContentAt(editor.state.selection.$from.pos, {
      type: BlockNodes.RawHtml,
      attrs: { html: result.html, uid: uuid() },
    })
    .run();
}
